import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    NumberField,
    EditButton,
    Create,
    Edit,
    SimpleForm,
    TextInput,
    DateInput,
    BooleanInput,
    FunctionField,
    required,
} from 'react-admin';
import { Alert, Chip } from '@mui/material';
import { FormToolbar } from '../../common/FormToolbar';

/**
 * Academic years.
 *
 * The first thing a school sets up, because a class section belongs to a year
 * and a student belongs to a section. Nothing else in the roster can exist
 * until one of these does.
 *
 * Small screens on purpose -- a school creates one of these a year.
 */

const YearList = props => (
    <List
        title="Academic years"
        {...props}
        sort={{ field: 'starts_on', order: 'DESC' }}
        exporter={false}
    >
        <Datagrid rowClick="edit" bulkActionButtons={false}>
            <EditButton />
            <TextField source="name" label="Year" />
            <DateField source="starts_on" label="Starts" />
            <DateField source="ends_on" label="Ends" />
            <FunctionField
                label="Current"
                sortBy="is_current"
                render={(r: any) =>
                    r.is_current ? <Chip size="small" label="current" color="success" /> : null
                }
            />
            <NumberField source="section_count" label="Sections" />
        </Datagrid>
    </List>
);

/**
 * `is_current` is what every "this year" query filters on, and the API clears
 * the flag on every other row when this one is saved. Saying so on the form
 * matters: an administrator ticking it has no other way to know they are also
 * un-ticking last year.
 */
const YearForm = () => (
    <>
        <TextInput
            source="name"
            label="Year"
            validate={[required()]}
            helperText="For example 2026-27"
            sx={{ maxWidth: 240 }}
        />
        <DateInput source="starts_on" label="Starts on" validate={[required()]} />
        <DateInput source="ends_on" label="Ends on" validate={[required()]} />
        <BooleanInput
            source="is_current"
            label="This is the current year"
            helperText="Only one year can be current. Ticking this clears it from any other year."
        />
    </>
);

const YearCreate = props => (
    <Create {...props} title="New academic year" redirect="list">
        <SimpleForm toolbar={<FormToolbar {...props} />}>
            <Alert severity="info" sx={{ mb: 2, width: '100%' }}>
                A year on its own does nothing. Add class sections to it next &mdash; students
                belong to sections, not to years.
            </Alert>
            <YearForm />
        </SimpleForm>
    </Create>
);

const YearEdit = props => (
    <Edit {...props} title="Academic year" mutationMode="pessimistic">
        <SimpleForm toolbar={<FormToolbar {...props} hasDelete />}>
            <YearForm />
        </SimpleForm>
    </Edit>
);

export default { list: YearList, create: YearCreate, edit: YearEdit };
